import {
    SPACING_BASE,
    RADIUS,
    FONT_PRIMARY,
    FONT_SECONDARY,
    BREAKPOINTS,
    COLOR_RED,
    COLOR_GREEN,
    COLOR_YELLOW,
    COLOR_STRONG_YELLOW,
    COLOR_VIOLET_DARKEST,
    COLOR_VIOLET_DARKER,
    COLOR_DEEP_BLUE,
    COLOR_BLUE_LINK,
    COLOR_BLUE_PRIMARY,
    COLOR_BLUE_NEON,
    COLOR_BLUE_NEON_LUMINATING,
    COLOR_RED_DEEP,
    COLOR_PRIMARY,
    PointerVariant,
} from './constants';
import colors from './colors';
import color from 'color';

export type BreakpointName = keyof typeof BREAKPOINTS;

// Exhaustive, mutually exclusive pointer classes: because every branch is
// conditioned on exactly one `pointer` value, up() is the exact complement of
// down() branch-by-branch. Keyboard-only devices (pointer: none) are treated
// as desktop-like.
const POINTER_CLASSES: ReadonlyArray<{ mq: string, variant: PointerVariant }> = [
    { mq: '(pointer: fine)', variant: 'fine' },
    { mq: '(pointer: coarse)', variant: 'coarse' },
    { mq: '(pointer: none)', variant: 'fine' },
];

// Manual-testing override: localStorage.setItem('weavr:forcePointer', 'coarse'|'fine')
// + reload collapses every query to the forced pointer class.
const forcedPointer = (): PointerVariant | null => {
    try {
        const value = window.localStorage.getItem('weavr:forcePointer');

        return value === 'fine' || value === 'coarse' ? value : null;
    } catch {
        return null;
    }
};

const buildPointerQuery = (condition: (variant: PointerVariant) => string): string => {
    const forced = forcedPointer();
    if (forced) {
        return `@media ${condition(forced)}`;
    }

    return `@media ${POINTER_CLASSES
        .map(({ mq, variant }) => `${mq} and ${condition(variant)}`)
        .join(', ')}`;
};

export const mediaDown = (breakpoint: BreakpointName): string => {
    const { fine, coarse } = BREAKPOINTS[breakpoint];
    if (fine === coarse) {
        return `@media (max-width: ${coarse}px)`;
    }

    return buildPointerQuery(variant => `(max-width: ${BREAKPOINTS[breakpoint][variant]}px)`);
};

export const mediaUp = (breakpoint: BreakpointName): string => {
    const { fine, coarse } = BREAKPOINTS[breakpoint];
    if (fine === coarse) {
        return `@media (min-width: ${coarse + 1}px)`;
    }

    return buildPointerQuery(variant => `(min-width: ${BREAKPOINTS[breakpoint][variant] + 1}px)`);
};

export const mediaBetween = (from: BreakpointName, to: BreakpointName): string =>
    buildPointerQuery(variant =>
        `(min-width: ${BREAKPOINTS[from][variant] + 1}px) and (max-width: ${BREAKPOINTS[to][variant]}px)`);

export const MEDIA_TOUCH = '@media (pointer: coarse)';

// Phone held in landscape: a coarse pointer with very little vertical room.
// Tablets in landscape keep more height, so the short max-height keeps them out.
// Honors the weavr:forcePointer override so it can be exercised in DevTools.
export const MEDIA_PHONE_LANDSCAPE = (() => {
    const orientation = '(orientation: landscape) and (max-height: 500px)';
    const forced = forcedPointer();
    if (forced === 'fine') {
        // Forced fine → never treat as a phone.
        return '@media (max-width: 0px)';
    }
    if (forced === 'coarse') {
        return `@media ${orientation}`;
    }

    return `@media (pointer: coarse) and ${orientation}`;
})();

export type ThemeProps = {
  colors: {
    black: string,
    white: string,
    red: string,
    red_transparent: string,
    deep_red: string,
    green: string,
    green_transparent: string,
    yellow: string,
    strong_yellow: string,
    transp_yellow_hightlight: string,
    violet_darkest: string,
    violet_darker: string,
    violet_primary: string,
    deep_blue: string,
    tab_white: string,
    blue_link: string,
    blue_primary: string,
    blue_neon: string,
    blue_neon_luminating: string,
    grey_text: string,
    grey_darker: string,
    grey_light: string,
    grey_background: string,
    transp_violet: string,
    transp_grey_light: string,
    grey_lighter: string,
    icon_background: string,
    transp_blue_darker: string,
    transp_blue_disabled: string,
    transp_blue: string,
    // Semantic tokens
    text_heading: string,
    text_muted_purple: string,
    text_subtle_purple: string,
    surface_elevated: string,
    surface_page: string,
    surface_alt: string,
    border_subtle: string,
    error_bg: string,
    error_text_dark: string,
    indigo_primary: string,
    teal_accent: string,
    tree_node_bg: string,
    tree_node_accent: string,
  },
  font: {
    primary: string,
    secondary: string,
  },
    spacing: (multiplier: number) => number,
    radius: number,
    up: (breakpoint: BreakpointName) => string,
    down: (breakpoint: BreakpointName) => string,
    between: (from: BreakpointName, to: BreakpointName) => string,
    touch: string,
    phoneLandscape: string,
    isDark: boolean,
}


const darkColors = {
    primary: COLOR_PRIMARY,
    black: '#e8e8f0',
    white: '#1e1e2e',
    red: COLOR_RED,
    red_transparent: color(COLOR_RED).fade(0.33).string(),
    deep_red: COLOR_RED_DEEP,
    green: COLOR_GREEN,
    green_transparent: color(COLOR_GREEN).fade(0.33).string(),
    yellow: COLOR_YELLOW,
    strong_yellow: COLOR_STRONG_YELLOW,
    transp_yellow_hightlight: color(COLOR_YELLOW).fade(0.62).string(),
    violet_darkest: COLOR_VIOLET_DARKEST,
    violet_darker: COLOR_VIOLET_DARKER,
    violet_primary: '#9080d8',
    deep_blue: COLOR_DEEP_BLUE,
    tab_white: '#252538',
    blue_link: COLOR_BLUE_LINK,
    blue_primary: COLOR_BLUE_PRIMARY,
    blue_neon: COLOR_BLUE_NEON,
    blue_neon_luminating: COLOR_BLUE_NEON_LUMINATING,
    grey_text: '#a8a8c8',
    grey_darker: '#4a4a6a',
    grey_light: '#2a2a3f',
    grey_background: '#0f0f1a',
    transp_violet: color('#5f4b8b').fade(0.8).string(),
    transp_grey_light: color('#252535').fade(0.35).string(),
    grey_lighter: '#252535',
    icon_background: '#1e1e35',
    transp_blue_darker: color(COLOR_BLUE_NEON).fade(0.68).string(),
    transp_blue_disabled: color(COLOR_BLUE_PRIMARY).fade(0.8).string(),
    transp_blue: color(COLOR_BLUE_NEON).fade(0.88).string(),
    // Semantic tokens — dark variants
    text_heading: '#d0c8f8',
    text_muted_purple: '#9994b5',
    text_subtle_purple: '#8899cc',
    surface_elevated: '#1e1e35',
    surface_page: '#141420',
    surface_alt: '#1a1a28',
    border_subtle: '#2a2a42',
    error_bg: 'rgba(180, 35, 24, 0.12)',
    error_text_dark: '#ff8080',
    indigo_primary: '#7c70d0',
    teal_accent: '#13c2c2',
    tree_node_bg: '#3a2460',
    tree_node_accent: '#9d7aff',
};

const buildTheme = (themeColors: typeof colors, isDark = false) => ({
    colors: themeColors,
    isDark,
    spacing: (multiplier: number) => SPACING_BASE * multiplier,
    radius: RADIUS,
    up: mediaUp,
    down: mediaDown,
    between: mediaBetween,
    touch: MEDIA_TOUCH,
    phoneLandscape: MEDIA_PHONE_LANDSCAPE,
    font: {
        primary: FONT_PRIMARY,
        secondary: FONT_SECONDARY,
    },
    palette: {
        mode: isDark ? 'dark' : 'light',
        primary: {
            main: themeColors.violet_primary,
        },
        secondary: {
            main: themeColors.blue_primary,
        },
        error: {
            main: themeColors.red,
        },
        warning: {
            main: themeColors.yellow,
        },
        success: {
            main: themeColors.green,
        },
        info: {
            main: themeColors.blue_primary,
        },
        text: {
            primary: themeColors.grey_text,
            secondary: themeColors.grey_text,
        },
        background: {
            default: themeColors.grey_background,
            paper: themeColors.white,
        },
    },
});

export type AppThemeMode = 'light' | 'dark';

export const createAppTheme = (mode: AppThemeMode) =>
    buildTheme(mode === 'dark' ? darkColors : colors, mode === 'dark');

const jssTheme = buildTheme(colors);

export default jssTheme;
